import React, { useState, useEffect } from 'react';
import { toast } from 'components/Toast';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useEmployee } from 'context/EmployeeContext';
import { getEmployeeDocuments, downloadEmployeeDocument, getEmployeeProfile } from 'services/employeePortal';

const ONBOARDING_API = 'https://onboarding-api.payrollnoot.com';

const fmtDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }); }
    catch { return val; }
};

const Paginator = ({ items, rowsPerPage, setRowsPerPage, page, setPage }) => {
    const totalPages = Math.ceil(items.length / rowsPerPage);
    return (
        <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, fontSize: 13, color: '#6c757d' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ whiteSpace: 'nowrap' }}>Rows per page:</span>
                <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                    style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 28px 4px 10px', fontSize: 13, color: '#333', background: '#fff', cursor: 'pointer', appearance: 'auto', minWidth: 64 }}>
                    {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
            </div>
            <span style={{ whiteSpace: 'nowrap' }}>
                {items.length === 0 ? '0 of 0' : `${(page - 1) * rowsPerPage + 1}–${Math.min(page * rowsPerPage, items.length)} of ${items.length}`}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {[
                    { label: '«', action: () => setPage(1), disabled: page === 1 },
                    { label: '‹', action: () => setPage(p => Math.max(1, p - 1)), disabled: page === 1 },
                    { label: '›', action: () => setPage(p => Math.min(totalPages, p + 1)), disabled: page === totalPages || totalPages === 0 },
                    { label: '»', action: () => setPage(totalPages), disabled: page === totalPages || totalPages === 0 },
                ].map(({ label, action, disabled }) => (
                    <button key={label} onClick={action} disabled={disabled}
                        style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #dee2e6', background: disabled ? '#f8f9fa' : '#fff', color: disabled ? '#bbb' : '#495057', cursor: disabled ? 'default' : 'pointer', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );
};

const Documents = () => {
    const { employeeId } = useEmployee();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'my-documents' ? 1 : 0);

    // Tax documents (W-2, 1099)
    const [taxPage, setTaxPage] = useState(1);
    const [taxRows, setTaxRows] = useState(10);
    const [downloading, setDownloading] = useState(null);

    const { data: taxDocs = [], isLoading: taxLoading } = useQuery({
        queryKey: ['ep-documents', employeeId],
        queryFn: () => getEmployeeDocuments(employeeId),
        enabled: !!employeeId,
    });

    // My Documents (admin-uploaded via onboarding API)
    const [myDocs, setMyDocs] = useState([]);
    const [myDocsLoading, setMyDocsLoading] = useState(false);
    const [myPage, setMyPage] = useState(1);
    const [myRows, setMyRows] = useState(10);
    const [myDownloading, setMyDownloading] = useState(null);

    const { data: profile } = useQuery({
        queryKey: ['ep-profile', employeeId],
        queryFn: () => getEmployeeProfile(employeeId),
        enabled: !!employeeId,
    });

    useEffect(() => {
        if (activeTab !== 1 || !profile?.email) return;
        setMyDocsLoading(true);
        const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
        fetch(`${ONBOARDING_API}/api/employee-documents?email=${encodeURIComponent(profile.email)}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(d => setMyDocs(Array.isArray(d) ? d : []))
            .catch(() => setMyDocs([]))
            .finally(() => setMyDocsLoading(false));
    }, [activeTab, profile?.email]);

    const handleDownloadTax = async (documentId, label) => {
        setDownloading(documentId);
        try {
            const res = await downloadEmployeeDocument(documentId);
            if (res?.url) window.open(res.url, '_blank');
            else toast('Download link not available.', 'warning');
        } catch { toast('Failed to download document.', 'error'); }
        finally { setDownloading(null); }
    };

    const handleDownloadMy = async (doc) => {
        setMyDownloading(doc.id);
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
            const url = doc.fileUrl || doc.url;
            if (url) { window.open(url, '_blank'); return; }
            const res = await fetch(`${ONBOARDING_API}/api/employee-documents/${doc.id}/download`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (data?.url) window.open(data.url, '_blank');
            else toast('Download link not available.', 'warning');
        } catch { toast('Failed to download document.', 'error'); }
        finally { setMyDownloading(null); }
    };

    const taxPaginated = taxDocs.slice((taxPage - 1) * taxRows, taxPage * taxRows);
    const myPaginated = myDocs.slice((myPage - 1) * myRows, myPage * myRows);

    const tabStyle = (active) => ({
        padding: '10px 22px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
        borderBottom: active ? '2px solid #1a9fbb' : '2px solid transparent',
        background: 'transparent', color: active ? '#1a9fbb' : '#6c757d',
    });

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h4 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 4 }}>Documents</h4>
            <p style={{ color: '#6c757d', marginBottom: 16, fontSize: 14 }}>
                View and download your tax documents and files shared by your employer.
            </p>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6', marginBottom: 20, background: '#fff', borderRadius: '8px 8px 0 0', padding: '0 4px' }}>
                <button style={tabStyle(activeTab === 0)} onClick={() => setActiveTab(0)}>📋 Tax Documents</button>
                <button style={tabStyle(activeTab === 1)} onClick={() => setActiveTab(1)}>📁 My Documents</button>
            </div>

            {/* Tax Documents Tab */}
            {activeTab === 0 && (
                <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                        <thead>
                            <tr style={{ background: '#f0f9fb' }}>
                                {['Label', 'Jurisdiction', 'Category', 'Year', 'Filed On', 'Action'].map(h => (
                                    <th key={h} style={{ padding: '14px 16px', textAlign: 'left', color: '#1a9fbb', fontWeight: 600, fontSize: 13 }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {taxLoading ? (
                                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>⏳ Loading...</td></tr>
                            ) : taxPaginated.length === 0 ? (
                                <tr>
                                    <td colSpan={6} style={{ padding: '40px 20px', textAlign: 'center' }}>
                                        <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
                                        <div style={{ fontWeight: 600, color: '#555', marginBottom: 6 }}>No tax documents found</div>
                                        <div style={{ fontSize: 13, color: '#aaa' }}>W-2s and 1099s will appear here once filed.</div>
                                    </td>
                                </tr>
                            ) : taxPaginated.map((d) => {
                                const docId = d.id ?? d.uuid;
                                const label = d.title ?? d.label ?? d.name ?? d.documentType ?? '—';
                                const filedOn = d.createdAtUtc ?? d.filedAt ?? d.filed_at ?? d.createdAt ?? d.created_at;
                                return (
                                    <tr key={docId} style={{ borderTop: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 500 }}>{label}</td>
                                        <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.jurisdiction ?? d.box15StateCode ?? d.state ?? '—'}</td>
                                        <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.category ?? d.documentType ?? '—'}</td>
                                        <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.taxYear ?? d.year ?? '—'}</td>
                                        <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmtDate(filedOn)}</td>
                                        <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                            <button onClick={() => handleDownloadTax(docId, label)} disabled={downloading === docId}
                                                style={{ background: downloading === docId ? '#ccc' : '#1a9fbb', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                                                {downloading === docId ? 'Downloading...' : 'Download'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                    <Paginator items={taxDocs} rowsPerPage={taxRows} setRowsPerPage={setTaxRows} page={taxPage} setPage={setTaxPage} />
                </div>
            )}

            {/* My Documents Tab */}
            {activeTab === 1 && (
                <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 380 }}>
                        <thead>
                            <tr style={{ background: '#f0f9fb' }}>
                                {['File Name', 'Type', 'Uploaded On', 'Action'].map(h => (
                                    <th key={h} style={{ padding: '14px 16px', textAlign: 'left', color: '#1a9fbb', fontWeight: 600, fontSize: 13 }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {myDocsLoading ? (
                                <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>⏳ Loading...</td></tr>
                            ) : myPaginated.length === 0 ? (
                                <tr>
                                    <td colSpan={4} style={{ padding: '40px 20px', textAlign: 'center' }}>
                                        <div style={{ fontSize: 40, marginBottom: 12 }}>📁</div>
                                        <div style={{ fontWeight: 600, color: '#555', marginBottom: 6 }}>No documents yet</div>
                                        <div style={{ fontSize: 13, color: '#aaa' }}>Documents shared by your employer will appear here.</div>
                                    </td>
                                </tr>
                            ) : myPaginated.map((doc) => (
                                <tr key={doc.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 500 }}>
                                        <span style={{ marginRight: 6 }}>📄</span>
                                        {doc.fileName || doc.label || doc.name || `Document #${doc.id}`}
                                    </td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                        <span style={{ background: '#e8f4f8', color: '#1a9fbb', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500 }}>
                                            {doc.documentType || doc.category || 'Document'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmtDate(doc.uploadedAt || doc.createdAt)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                        <button onClick={() => handleDownloadMy(doc)} disabled={myDownloading === doc.id}
                                            style={{ background: myDownloading === doc.id ? '#ccc' : '#1a9fbb', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                                            {myDownloading === doc.id ? 'Downloading...' : 'Download'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    <Paginator items={myDocs} rowsPerPage={myRows} setRowsPerPage={setMyRows} page={myPage} setPage={setMyPage} />
                </div>
            )}
        </div>
    );
};

export default Documents;

